/**
 * cart-timer.js
 * Shared reservation countdown timer for the cart page.
 *
 * Exposed as:  window.CartTimer
 * Duration:    CART_TIMER_MINUTES (15 min — confirmed with BE)
 *
 * Expiry behaviour:
 *   - Calls CartCoupon.removeApplied(true) — silent: no fetchTimer/refreshCart
 *   - Calls CartPoints.removeApplied(true)  — silent: no fetchTimer/refreshCart
 *   - Clears Shopify cart and reloads
 *
 * Shared utilities (used by coupon and points modules):
 *   window.CartUtils.refreshCart()         — re-fetch /cart.js, update subtotal + discount rows
 *   window.CartUtils.recalcTotalSavings()  — recompute displayed subtotal and total savings
 */

(function (global) {
  'use strict';

  var CART_TIMER_MINUTES = 4320 // 3 days

  var _interval = null;

  function _el(sel) { return document.querySelector(sel); }

  /* ─── Money / HTML helpers ──────────────────────────────────────────────── */
  function _fmt(cents) {
    var format = (global.cartConfig && global.cartConfig.moneyFormat) || 'RM{{amount}}';
    var amount = (cents / 100).toFixed(2);
    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', String(Math.floor(cents / 100)))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', String(Math.floor(cents / 100)));
  }

  function _esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ─── Render ────────────────────────────────────────────────────────────── */
  function _render(remainingMs) {
    var cd = _el('[data-timer-countdown]');
    if (!cd) return;
    var s  = Math.max(0, Math.ceil(remainingMs / 1000));
    var m  = Math.floor(s / 60).toString().padStart(2, '0');
    var ss = (s % 60).toString().padStart(2, '0');
    cd.textContent = m + ':' + ss;
  }

  /* ─── Core tick ─────────────────────────────────────────────────────────── */
  function _tick(expiryMs) {
    var remaining = expiryMs - Date.now();
    if (remaining <= 0) { _expire(); return; }
    _render(remaining);
  }

  function _launch(expiryMs) {
    clearInterval(_interval);
    // Timer element is intentionally kept hidden — logic still runs for expiry detection.
    // var wrap = _el('[data-reservation-timer]');
    // if (wrap) wrap.style.display = 'flex';
    _tick(expiryMs);
    _interval = setInterval(function () { _tick(expiryMs); }, 1000);
  }

  /* ─── Public API ────────────────────────────────────────────────────────── */

  function start() {
    var expiryMs = Date.now() + CART_TIMER_MINUTES * 60 * 1000;
    _launch(expiryMs);
  }

  /**
   * Start the timer from a server-side lock timestamp (server-synchronised
   * across page refreshes). Appends 'Z' so the string is parsed as UTC.
   */
  function startAt(timeApplied) {
    if (!timeApplied) { stop(); return; }
    var lockMs   = Date.parse(timeApplied.replace(' ', 'T') + 'Z');
    var expiryMs = lockMs + CART_TIMER_MINUTES * 60 * 1000;
    if (isNaN(expiryMs) || Date.now() >= expiryMs) { stop(); return; }
    _launch(expiryMs);
  }

  function stop() {
    clearInterval(_interval);
    _interval = null;
    var wrap = _el('[data-reservation-timer]');
    if (wrap) wrap.style.display = 'none';
  }

  function isRunning() { return _interval !== null; }

  /**
   * Returns true if the given server timestamp is past the reservation window.
   * Used by cart-points.js _init() to silently clean up stale locks on page load.
   */
  function isExpired(timeApplied) {
    if (!timeApplied) return true;
    var lockMs   = Date.parse(timeApplied.replace(' ', 'T') + 'Z');
    var expiryMs = lockMs + CART_TIMER_MINUTES * 60 * 1000;
    return isNaN(expiryMs) || Date.now() >= expiryMs;
  }

  /* ─── Expiry handler ────────────────────────────────────────────────────── */
  async function _expire() {
    clearInterval(_interval);
    _interval = null;

    var wrap = _el('[data-reservation-timer]');
    if (wrap) wrap.style.display = 'none';

    document.dispatchEvent(new CustomEvent('cart:timer-expired'));
    console.log('[CartTimer] Timer expired — unlocking coupons and points');

    // Pass silent=true so removeApplied skips fetchTimer/refreshCart —
    // we handle the refresh ourselves below after re-fetching server state.
    try {
      if (global.CartCoupon && typeof global.CartCoupon.removeApplied === 'function') {
        await global.CartCoupon.removeApplied(true);
      }
      if (global.CartPoints && typeof global.CartPoints.removeApplied === 'function') {
        await global.CartPoints.removeApplied(true);
      }
    } catch (err) {
      console.error('[CartTimer] Error during expiry unlock:', err);
    }

    // Cart items are kept — no clear or reload.
    // console.log('[CartTimer] Clearing cart and reloading');
    // fetch('/cart/clear.js', { method: 'POST' })
    //   .finally(function () { location.reload(); });

    // Re-fetch server lock state (backend has unlocked everything by now).
    // fetchTimer calls syncShopifyDiscounts([]) to clear Shopify discount codes.
    console.log('[CartTimer] Re-fetching lock state after expiry');
    try {
      if (global.CartLockManager) {
        var timerRes = await global.CartLockManager.fetchTimer();
        var codes = (timerRes && timerRes.data && timerRes.data.coupon_codes) || [];
        if (global.CartCoupon) global.CartCoupon.restoreFromCodes(codes);
        if (global.CartPoints) global.CartPoints.syncFromTimer(timerRes);
      }
      if (global.CartUtils) await global.CartUtils.refreshCart();
    } catch (err) {
      console.error('[CartTimer] Error refreshing after expiry:', err);
    }
  }

  /* ─── Shared cart utilities ─────────────────────────────────────────────── */
  var CartUtils = {

    /**
     * Write the cart data-attributes read by cart-points.js and cartCheckout().
     * Single writer shared by CartUtils.refreshCart() and cart-layout's
     * updateCartUI(), so the two paths cannot drift.
     *
     *   data-cart-redeemed-cents     Σ final_line_price of lines with properties.redeemed.
     *                                Points-redeemed gifts are not payable merchandise.
     *   data-cart-points-alloc-cents allocated amount of the POINTS* cart-level discount.
     *                                Added back by _redeemableBaseDollars() so the
     *                                redeemable base is invariant under the points lock
     *                                (otherwise the reconcile loop oscillates).
     *
     * Must run BEFORE recalcTotalSavings() and before cart:updated is dispatched.
     */
    writeCartAttrs: function (cart) {
      var el = _el('[data-cart-subtotal]');
      if (!el || !cart) return;

      var redeemedCents = 0;
      (cart.items || []).forEach(function (it) {
        if (it.properties && it.properties.redeemed) redeemedCents += it.final_line_price;
      });

      var pointsAllocCents = 0;
      (cart.cart_level_discount_applications || []).forEach(function (d) {
        if (d.title && d.title.indexOf('POINTS') === 0) pointsAllocCents += d.total_allocated_amount;
      });

      el.setAttribute('data-cart-subtotal-cents',      cart.items_subtotal_price);
      el.setAttribute('data-cart-total-cents',         cart.total_price);
      el.setAttribute('data-cart-original-cents',      cart.original_total_price);
      el.setAttribute('data-cart-redeemed-cents',      redeemedCents);
      el.setAttribute('data-cart-points-alloc-cents',  pointsAllocCents);
      el.setAttribute('data-cart-base-price',          (cart.total_price / 100).toFixed(2));
    },

    /**
     * Re-fetch /cart.js and update:
     *   - [data-cart-subtotal] data attributes (cents from Shopify)
     *   - [data-discount-rows] from cart.cart_level_discount_applications
     * Then calls recalcTotalSavings() to recompute the displayed subtotal.
     */
    refreshCart: async function () {
      try {
        var res = await fetch('/cart.js');
        if (!res.ok) return;
        var cart = await res.json();

        // Store raw Shopify cents so recalcTotalSavings can read them
        CartUtils.writeCartAttrs(cart);

        // Refresh Shopify discount rows (coupon codes applied via integration)
        var discountRowsEl = _el('[data-discount-rows]');
        if (discountRowsEl) {
          var dds = discountRowsEl.dataset;
          var strCashOffStars  = dds.strCashOffStars  || 'Cash Off ([points] Stars)';
          var strCashOff       = dds.strCashOff       || 'Cash Off';
          var strCouponLabel   = dds.strCouponLabel   || 'Coupon Code ([title])';
          var strDiscountLabel = dds.strDiscountLabel || 'Discount ([title])';
          discountRowsEl.innerHTML = '';
          var apps = cart.cart_level_discount_applications || [];
          if (apps.length > 0) {
            apps.forEach(function (d) {
              var row = document.createElement('div');
              row.className = 'flex jc-between ai-center';
              var label;
              if (d.title.indexOf('POINTS') === 0) {
                var pts = global.CartPoints ? global.CartPoints.getApplied() : null;
                label = pts ? strCashOffStars.replace('[points]', pts.points) : strCashOff;
              } else {
                var appliedCoupons = global.CartCoupon ? global.CartCoupon.getApplied() : [];
                var isCoupon = appliedCoupons.some(function (c) { return c.code === d.title; });
                label = isCoupon ? strCouponLabel.replace('[title]', _esc(d.title)) : strDiscountLabel.replace('[title]', _esc(d.title));
              }
              row.innerHTML =
                '<span class="p2 c-medium-grey tl">' + label + '</span>' +
                '<span class="p2 c-medium-grey tr">-' + _fmt(d.total_allocated_amount) + '</span>';
              discountRowsEl.appendChild(row);
            });
            discountRowsEl.classList.add('is-visible');
          } else {
            discountRowsEl.classList.remove('is-visible');
          }
        }

        // Re-render line items with server prices (line_price may change when discounts are applied/removed)
        if (global.CartItems) global.CartItems.applyCart(cart);

        CartUtils.recalcTotalSavings();
      } catch (err) {
        console.error('[CartUtils] refreshCart failed:', err);
      }
    },

    /**
     * Recompute [data-cart-subtotal] display and [data-total-savings].
     *
     * All discounts (coupons + points) are now real Shopify discount codes applied
     * via syncShopifyDiscounts(). Both are already reflected in data-cart-total-cents
     * (cart.total_price). No manual subtraction needed.
     */
    recalcTotalSavings: function () {
      var subtotalEl = _el('[data-cart-subtotal]');
      if (!subtotalEl) return;

      var subtotalCents = parseInt(subtotalEl.getAttribute('data-cart-subtotal-cents') || subtotalEl.getAttribute('data-cart-total-cents') || '0', 10);
      var totalCents    = parseInt(subtotalEl.getAttribute('data-cart-total-cents')    || '0', 10);
      var originalCents = parseInt(subtotalEl.getAttribute('data-cart-original-cents') || '0', 10);

      // [data-points-discount-row] removed — points discount is a real Shopify discount
      // code now and is already included in totalCents. No separate subtraction needed.
      // var pointsRM = 0;
      // var pointsRow = _el('[data-points-discount-row]');
      // var pointsAmt = _el('[data-points-discount-amount]');
      // if (pointsRow && pointsAmt && pointsRow.style.display !== 'none') {
      //   var v = parseFloat(String(pointsAmt.textContent).replace(/[^0-9.]/g, ''));
      //   if (!isNaN(v)) pointsRM = v;
      // }

      // Total savings = original minus post-all-discounts (coupons + points combined)
      var totalSavingsRM = (originalCents - totalCents) / 100;

      // Subtotal shows the pre-cart-discount merchandise total (items_subtotal_price);
      // coupons/points appear as discount rows and bring it down to Total.
      subtotalEl.textContent = _fmt(subtotalCents);

      var totalEl = _el('[data-cart-total]');
      if (totalEl) totalEl.textContent = _fmt(totalCents);

      // Total savings row
      var savingsRow = _el('[data-total-savings]');
      var savingsAmt = _el('[data-total-savings-amount]');
      if (savingsRow && savingsAmt) {
        if (totalSavingsRM > 0) {
          savingsAmt.textContent   = '- ' + _fmt(originalCents - totalCents);
          savingsRow.style.display = 'flex';
        } else {
          savingsRow.style.display = 'none';
        }
      }

      CartUtils.recalcPointsEarned();
    },

    recalcPointsEarned: function () {
      var el = _el('[data-points-earned]');
      if (!el) return;

      var subtotalEl = _el('[data-cart-subtotal]');
      if (!subtotalEl) return;

      var totalCents = parseInt(subtotalEl.getAttribute('data-cart-total-cents') || '0', 10);
      var totalMajor = Math.floor(totalCents / 100);
      var levelId    = parseInt(el.getAttribute('data-points-level-id') || '1', 10);
      var currency   = (el.getAttribute('data-points-currency') || '').toUpperCase();
      var pointsPerCurrencyLevel1 = parseFloat(el.getAttribute('data-points-per-currency-level-1') || '0');
      var pointsPerCurrencyLevel2 = parseFloat(el.getAttribute('data-points-per-currency-level-2') || '0');
      var pointsPerCurrencyLevel3 = parseFloat(el.getAttribute('data-points-per-currency-level-3') || '0');
      var isPink     = levelId === 3;
      var points     = 0;
      console.log('[recalcPointsEarned]', { levelId: levelId, currency: currency, totalMajor: totalMajor, isPink: isPink });

      if (currency === 'CNY' || currency === 'HKD' || currency === 'MOP') {
        points = isPink ? totalMajor * pointsPerCurrencyLevel3 : totalMajor * pointsPerCurrencyLevel1;
      } else if (currency === 'TWD') {
        points = isPink ? Math.floor(totalMajor / 1) * pointsPerCurrencyLevel3 : Math.floor(totalMajor / 1) * pointsPerCurrencyLevel1;
      } else if (currency === 'SGD') {
        points = isPink ? totalMajor * pointsPerCurrencyLevel3 : totalMajor * pointsPerCurrencyLevel1;
      } else if (currency === 'MYR') {
        points = isPink ? totalMajor * pointsPerCurrencyLevel3 : totalMajor * pointsPerCurrencyLevel1;
      }

      console.log('[recalcPointsEarned] points =', points);
      var earnedTmpl = el.dataset.strEarned || 'Earn [points] Stars with this order';
      el.innerHTML = '<span class="c-logo-pink">✶✶✶</span> ' + earnedTmpl.replace('[points]', points) + ' <span class="c-logo-pink">✶✶✶</span>';
    },

    // recalcDiscountBox removed — [data-discount-box] is no longer in the template.
    // Points discount shows in [data-discount-rows] as a real Shopify discount code.
    // recalcDiscountBox: function () {
    //   var box = _el('[data-discount-box]');
    //   if (!box) return;
    //   var row     = _el('[data-points-discount-row]');
    //   var visible = !!(row && row.style.display !== 'none');
    //   box.style.display = visible ? 'flex' : 'none';
    // },

    formatMoney: _fmt,
  };

  /* ─── Exports ───────────────────────────────────────────────────────────── */
  global.CartTimer = {
    start: start, startAt: startAt, stop: stop,
    isRunning: isRunning, isExpired: isExpired,
  };
  global.CartUtils = CartUtils;

})(window);
